// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ApiParameter, get, HasPagingParameters, post } from "@insite/client-framework/Services/ApiService";
import { BaseModel, PaginationModel, ProductLineModel } from "@insite/client-framework/Types/ApiModels";

const serviceUri = "api/guest/backorder";
const selectAltWarehouseUri = "/api/guest/altwarehouse/SelectAltWarehouse";
const removeAltWarehouseUri = "/api/guest/altwarehouse/RemoveAltWarehouse";

export interface BackOrderModel extends ApiParameter, ProductLineModel {
    customerNumber: string;
    orderLineNumber: number;
    orderLineStatus: string;
    orderNumber: string;
    productDescription: string;
    productErpNumber: string;
    warehouse: string;
    poNumber: string;
    backorderQuantity: string;
    cancelRequested: boolean;
    availableAltWarehouse: string;
    isShippedFromAlternateDc: boolean;
}

export interface BackOrdersCollection extends BaseModel {
    backorders: BackOrderModel[];
    defaultPageSize: number;
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    pagination: PaginationModel | null;
}

export interface GetBackOrdersParameters extends ApiParameter, HasPagingParameters {
    OrderNumber?: string;
    PoNumber?: string;
    preventCaching?: boolean;
}

export interface CancelProductParameters {
    OrderNumber?: string;
    OrderLineNumber?: number;
    CustomerNumber?: string;
    Warehouse?: string;
    ItemNumber?: string;
}

export async function getBackOrders(params: GetBackOrdersParameters) {
    try {
        const result = await get<any>(`${serviceUri}`, getBackOrdersParams(params));
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: error,
        };
    }
}

function getBackOrdersParams(parameters: GetBackOrdersParameters): any {
    const params: any = {};
    Object.keys(parameters).forEach((param) => {
        if (param === "preventCaching") {
            params.t = Date.now();
        } else {
            params[param] = parameters[param as keyof typeof parameters];
        }
    });

    return params;
}

export async function cancelBackOrder(backOrder: BackOrderModel) {
    try {
        const data = {
            OrderNumber: backOrder.orderNumber,
            OrderLineNumber: backOrder.orderLineNumber,
            CustomerNumber: backOrder.customerNumber,
            Warehouse: backOrder.warehouse,
            ItemNumber:backOrder.productErpNumber
        };
        const result = await post<any>(`${serviceUri}`, data);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function selectAltWarehouse(backOrder: BackOrderModel) {
    try {
        const param = {
            "id": backOrder?.id,
            "isCartLine": false
        };
        const result = await post<any>(`${selectAltWarehouseUri}`, param);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: error,
        };
    }
}

export async function removeAltWarehouse(backOrder: BackOrderModel) {
    try {
        const param = {
            "id": backOrder?.id,
        };
        const result = await post<any>(`${removeAltWarehouseUri}`, param);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: error,
        };
    }
}
